import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box, Typography, IconButton,
  InputLabel, FormControl, Select, MenuItem, CircularProgress,
  alpha, Paper, Autocomplete, Alert
} from '@mui/material';
import { Trash2, Plus, Type, ShoppingBag, Building2, MapPin } from 'lucide-react';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import { useDispatch, useSelector } from 'react-redux';
import { createProduct, updateProduct } from '../redux/slices/productSlice';
import { fetchUsers } from '../redux/slices/userSlice';
import { fetchAllBusinessesForDropdown } from '../redux/slices/businessSlice';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getProductImageUrl } from '../utils/helpers';

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['clean']
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet'
];

const ProductModal = ({ open, onClose, product, categories, subCategories, countries, states, cities, brands, onSuccess }) => {
  const dispatch = useDispatch();
  const isEdit = !!product;

  // Pull allBusinesses from Redux store
  const { allBusinesses } = useSelector((state) => state.business);

  const [formData, setFormData] = useState({
    product_name: '', slug: '', user_id: '', brand_name: '',
    category: '', sub_category: '', country: 'India',
    state: '', city: '', zipCode: '', address: '', description: '',
    price: '', policyStatus: false, product_keywords: ''
  });

  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);

  // ── Business selector state ─────────────────────────────────────────────
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');

  // Fetch businesses for the dropdown when modal opens (add mode only)
  useEffect(() => {
    if (!isEdit && open) {
      dispatch(fetchAllBusinessesForDropdown(businessSearchTerm));
    }
  }, [businessSearchTerm, open, isEdit, dispatch]);

  // When a business is selected and its user_id is a raw string (not populated),
  // fetch the user details so the Autocomplete can display them properly.
  useEffect(() => {
    const ownerId = formData.user_id;
    if (!ownerId || selectedUser) return;          // already resolved, skip
    if (typeof ownerId !== 'string') return;

    dispatch(fetchUsers({ page: 1, limit: 50, search: '' }))
      .unwrap()
      .then(res => {
        const found = (res.users || []).find(u => u._id === ownerId);
        if (found) {
          setSelectedUser(found);
        }
      })
      .catch(() => {});
  // Only run when user_id changes (not on every selectedUser change)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.user_id]);

  useEffect(() => {
    if (product && open) {
      setFormData({
        product_name: product.product_name || '',
        slug: product.slug || '',
        // Normalize: a populated user_id object would otherwise serialize to
        // "[object Object]" in the payload and overwrite the real owner.
        user_id: (product.user_id?._id || product.user_id) || '',
        brand_name: product.brand_name || '',
        category: product.category || '',
        sub_category: product.sub_category || '',
        country: product.country || '',
        state: product.state || '',
        city: product.city || '',
        zipCode: product.zipCode || '',
        address: product.address || '',
        description: product.description || '',
        price: product.price || '',
        policyStatus: product.policyStatus !== undefined ? product.policyStatus : false,
        product_keywords: product.product_keywords || ''
      });
      setExistingImages(product.product_image || []);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setSelectedUser(null);
    } else if (open) {
      setFormData({
        product_name: '', slug: '', user_id: '', brand_name: '',
        category: '', sub_category: '', country: 'India',
        state: '', city: '', zipCode: '', address: '', description: '',
        price: '', policyStatus: false, product_keywords: ''
      });
      setExistingImages([]);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setSelectedUser(null);
      setSelectedBusiness(null);
      setBusinessSearchTerm('');
    }
    setError('');
  }, [product, open]);

  // Clean up object URLs on unmount or when previews change
  useEffect(() => {
    return () => {
      newImagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [newImagePreviews]);

  // ── Slug generator ─────────────────────────────────────────────
  const generateSlug = (name = '', city = '') => {
    const toSlug = (str) =>
      str
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')   // remove special chars
        .replace(/\s+/g, '-')            // spaces → hyphens
        .replace(/-+/g, '-')             // collapse multiple hyphens
        .replace(/^-|-$/g, '');          // trim leading/trailing hyphens

    const namePart = toSlug(name);
    const cityPart = toSlug(city);
    return [namePart, cityPart].filter(Boolean).join('-');
  };

  const selectedCat = categories?.find(c => c._id === formData.category || c._id === formData.category?._id);
  const isServiceCategory = selectedCat?.category_name?.toLowerCase().includes('service');

  const brandOptions = Array.from(
    new Set([
      ...(formData.brand_name ? [formData.brand_name] : []),
      ...(brands || []).map((b) => (typeof b === 'string' ? b : b?.brand_name)).filter(Boolean)
    ])
  );

  const handleChange = (e) => {
    const selectFields = ['category', 'sub_category', 'country', 'state', 'city', 'policyStatus'];
    const isSelect = selectFields.includes(e.target.name);
    if (e.target.name === 'category') {
      setFormData({ ...formData, category: e.target.value, sub_category: '' });
    } else if (e.target.name === 'product_name') {
      // Auto-generate slug as product name is typed
      const newName = e.target.value.trimStart();
      setFormData(prev => ({
        ...prev,
        product_name: newName,
        slug: generateSlug(newName, prev.city),
      }));
    } else if (e.target.name === 'state') {
      const newState = e.target.value;
      setFormData(prev => ({
        ...prev,
        state: newState,
        city: '',
        slug: generateSlug(prev.product_name, '')
      }));
    } else if (e.target.name === 'city') {
      // Re-generate slug when city changes
      const newCity = e.target.value;
      setFormData(prev => ({
        ...prev,
        city: newCity,
        slug: generateSlug(prev.product_name, newCity),
      }));
    } else {
      const value = e.target.type === 'checkbox'
        ? e.target.checked
        : isSelect ? e.target.value : e.target.value.trimStart();
      setFormData({ ...formData, [e.target.name]: value });
    }
  };

  const handleImagesChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newPreviews = files.map(file => URL.createObjectURL(file));

      setNewImageFiles(prev => [...prev, ...files]);
      setNewImagePreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const removeExistingImage = (index) => {
    const updated = [...existingImages];
    updated.splice(index, 1);
    setExistingImages(updated);
  };

  const removeNewImage = (index) => {
    URL.revokeObjectURL(newImagePreviews[index]);
    const updatedFiles = [...newImageFiles];
    const updatedPreviews = [...newImagePreviews];
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
    setNewImageFiles(updatedFiles);
    setNewImagePreviews(updatedPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_name) return setError('Product name is required');
    if (!isEdit && existingImages.length === 0 && newImageFiles.length === 0) {
      return setError('At least one product image is required');
    }

    setLoading(true);
    try {
      const fd = new FormData();
      // Trim all string fields before submitting
      const trimmedFormData = {
        ...formData,
        product_name: formData.product_name.trim(),
        slug: formData.slug.trim(),
        brand_name: (formData.brand_name || '').trim(),
        zipCode: (formData.zipCode || '').trim(),
        address: (formData.address || '').trim(),
        product_keywords: (formData.product_keywords || '').trim(),
      };
      Object.keys(trimmedFormData).forEach(key => {
        fd.append(key, trimmedFormData[key]);
      });

      // Explicitly guarantee user_id is in payload (from business selection or manual pick)
      const resolvedUserId = formData.user_id ||
        selectedUser?._id ||
        (selectedBusiness?.user_id && typeof selectedBusiness.user_id === 'object'
          ? selectedBusiness.user_id._id
          : selectedBusiness?.user_id) || '';
      fd.set('user_id', resolvedUserId);  // fd.set overrides any earlier append

      // Handle images management
      if (isEdit) {
        fd.append('keep_images', JSON.stringify(existingImages));
      }

      if (newImageFiles.length > 0) {
        newImageFiles.forEach(file => fd.append('product_images', file));
      }

      if (isEdit) {
        fd.append('product_id', product._id);
        await dispatch(updateProduct(fd)).unwrap();
      } else {
        await dispatch(createProduct(fd)).unwrap();
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const norm = (str) => String(str || '').trim().toLowerCase();
  const selectedStateObj = (Array.isArray(states) ? states : []).find(
    s => norm(s.state_name) === norm(formData.state) || String(s._id) === String(formData.state)
  );
  const filteredCities = formData.state && Array.isArray(cities)
    ? Array.from(
        new Map(
          cities
            .filter(c => {
              if (norm(c.state?.state_name) === norm(formData.state)) return true;
              if (typeof c.state === 'string' && norm(c.state) === norm(formData.state)) return true;
              if (norm(c.state_name) === norm(formData.state)) return true;
              if (selectedStateObj && (
                String(c.state_id) === String(selectedStateObj._id) ||
                String(c.state_id?._id) === String(selectedStateObj._id) ||
                String(c.state?._id) === String(selectedStateObj._id)
              )) return true;
              return false;
            })
            .map(c => [norm(c.city_name), c])
        ).values()
      ).sort((a, b) => (a.city_name || '').localeCompare(b.city_name || ''))
    : (Array.isArray(cities) ? cities : []);

  const hasCurrentCityInFiltered = filteredCities.some(c => norm(c.city_name) === norm(formData.city));
  const displayCities = (!hasCurrentCityInFiltered && formData.city)
    ? [{ _id: 'current-city-fallback', city_name: formData.city }, ...filteredCities]
    : filteredCities;

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Product' : 'Create New Product'}
        subtitle={isEdit ? 'Update product pricing, images and inventory attributes' : 'Publish a new product listing to the marketplace'}
        icon={ShoppingBag}
        iconColor="primary"
        onClose={!loading ? onClose : undefined}
      />

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <AdminDialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box>
            {/* ── Business Selector (add mode only) ── */}
            {!isEdit && (
              <Box sx={{ mb: 5 }}>
                <Typography
                  variant="overline"
                  sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, letterSpacing: 1.5 }}
                >
                  <Building2 size={14} /> SELECT BUSINESS
                </Typography>

                <Autocomplete
                  options={allBusinesses}
                  getOptionLabel={(option) =>
                    `${option.business_name || ''} — ${option.city || ''}, ${option.state || ''}`
                  }
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  value={selectedBusiness}
                  onChange={(event, newValue) => {
                    setSelectedBusiness(newValue);
                    if (newValue) {
                      // Normalise user_id — backend may return a populated object or a raw string ID
                      const ownerObj = newValue.user_id && typeof newValue.user_id === 'object'
                        ? newValue.user_id   // populated: { _id, first_name, last_name, email, phone }
                        : null;
                      const ownerId  = ownerObj?._id || (typeof newValue.user_id === 'string' ? newValue.user_id : '') || '';

                      // Auto-fill location + owner + brand + slug fields
                      setFormData(prev => ({
                        ...prev,
                        country   : newValue.country        || prev.country,
                        state     : newValue.state          || prev.state,
                        city      : newValue.city           || prev.city,
                        zipCode   : newValue.zipCode        || prev.zipCode,
                        address   : newValue.address        || prev.address,
                        user_id   : ownerId                 || prev.user_id,
                        brand_name: newValue.business_name  || prev.brand_name,
                        // Regenerate slug with new city if product name already typed
                        slug      : generateSlug(prev.product_name, newValue.city || prev.city),
                      }));

                      if (ownerObj) {
                        setSelectedUser(ownerObj);
                      }
                    } else {
                      // Clear everything when business is deselected
                      setFormData(prev => ({
                        ...prev,
                        country: 'India', state: '', city: '', zipCode: '', address: '',
                        user_id: '', brand_name: ''
                      }));
                      setSelectedUser(null);
                    }
                  }}
                  onInputChange={(_, val) => setBusinessSearchTerm(val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and select a business"
                      helperText="Selecting a business will auto-fill address, city, state, zip and owner"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">{option.business_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[option.address, option.city, option.state, option.zipCode].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />

                {/* Pre-fill preview card */}
                {selectedBusiness && (
                  <Box
                    sx={{
                      mt: 2, p: 2.5, borderRadius: 2,
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.05),
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.info.main, 0.25),
                      display: 'flex', flexDirection: 'column', gap: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <MapPin size={15} color="#1890FF" />
                      <Typography variant="caption" fontWeight={800} color="info.main" sx={{ letterSpacing: 1 }}>
                        PRE-FILLED FROM BUSINESS
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                      {[
                        { label: 'Business', value: selectedBusiness.business_name },
                        { label: 'Address', value: selectedBusiness.address },
                        { label: 'City', value: selectedBusiness.city },
                        { label: 'State', value: selectedBusiness.state },
                        { label: 'Country', value: selectedBusiness.country },
                        { label: 'Zip Code', value: selectedBusiness.zipCode },
                      ].map(({ label, value }) => value ? (
                        <Box key={label}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{value}</Typography>
                        </Box>
                      ) : null)}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Basic Info */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="overline" sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'block', letterSpacing: 1.5 }}>BASIC INFORMATION</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <TextField
                  label="Product Name"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  label="Slug (URL) — Auto Generated"
                  name="slug"
                  value={formData.slug}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText={formData.slug
                    ? `✓ ${formData.slug}`
                    : 'Auto-fills as you type the product name'}
                  FormHelperTextProps={{
                    sx: { color: formData.slug ? 'info.main' : 'text.secondary', fontWeight: formData.slug ? 600 : 400 }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.neutral',
                      '& fieldset': { borderStyle: 'dashed' }
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <Autocomplete
                  freeSolo
                  options={brandOptions}
                  value={formData.brand_name || ''}
                  onChange={(_, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      brand_name: typeof newValue === 'string' ? newValue : (newValue?.brand_name || '')
                    }));
                  }}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === 'input') {
                      setFormData((prev) => ({
                        ...prev,
                        brand_name: newInputValue
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Brand Name"
                      name="brand_name"
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
                <TextField
                  label={isServiceCategory ? "Price (₹) (Optional for Service)" : "Price (₹)"}
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  fullWidth
                  required={!isServiceCategory}
                  helperText={isServiceCategory ? "Leave empty to show 'Price on Request'" : ""}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select name="category" value={formData.category} label="Category" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {categories?.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>{cat.category_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required disabled={!formData.category}>
                  <InputLabel>Sub Category</InputLabel>
                  <Select name="sub_category" value={formData.sub_category} label="Sub Category" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {(subCategories?.filter(sub => sub.category_id === formData.category) || []).map((sub) => (
                      <MenuItem key={sub._id} value={sub.sub_category_name}>{sub.sub_category_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Location & Details */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="overline" sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'block', letterSpacing: 1.5 }}>LOCATION & DETAILS</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>Country</InputLabel>
                  <Select name="country" value={formData.country} label="Country" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {countries?.map((c) => (
                      <MenuItem key={c._id} value={c.country_name}>{c.country_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select name="state" value={formData.state} label="State" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {states?.map((s) => (
                      <MenuItem key={s._id} value={s.state_name}>{s.state_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required disabled={!formData.state}>
                  <InputLabel>City</InputLabel>
                  <Select name="city" value={formData.city} label="City" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    {displayCities?.map((c) => (
                      <MenuItem key={c._id} value={c.city_name}>{c.city_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Zip Code" name="zipCode" value={formData.zipCode} onChange={handleChange} fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField label="Full Address" name="address" value={formData.address} onChange={handleChange} fullWidth required multiline rows={3} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="overline" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, letterSpacing: 1 }}>
                  <Type size={14} /> DESCRIPTION
                </Typography>
                <Box sx={{
                  '& .ql-container': { borderBottomLeftRadius: 12, borderBottomRightRadius: 12, minHeight: 200, fontSize: '1rem', fontFamily: 'inherit' },
                  '& .ql-toolbar': { borderTopLeftRadius: 12, borderTopRightRadius: 12, background: '#f8fafc' },
                  '& .ql-editor': { minHeight: 200 }
                }}>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <TextField label="Keywords" name="product_keywords" value={formData.product_keywords} onChange={handleChange} fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <FormControl fullWidth required>
                  <InputLabel>Policy Status</InputLabel>
                  <Select name="policyStatus" value={formData.policyStatus} label="Policy Status" onChange={handleChange} sx={{ borderRadius: 2 }}>
                    <MenuItem value={true}>Accepted</MenuItem>
                    <MenuItem value={false}>Not Accepted</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Product Images Upload Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ mb: 3, fontWeight: 700, color: 'text.secondary', display: 'block', letterSpacing: 1.5 }}>PRODUCT IMAGES</Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2
              }}>
                {/* Existing Images */}
                {existingImages.map((img, index) => (
                  <Paper
                    key={`existing-${index}`}
                    elevation={0}
                    sx={{
                      aspectRatio: '1/1',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      position: 'relative',
                      bgcolor: 'background.neutral',
                      '&:hover .delete-btn': { opacity: 1 }
                    }}
                  >
                    <img
                      src={getProductImageUrl(img)}
                      alt="product"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      className="delete-btn"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: alpha('#ef4444', 0.9), color: 'white',
                        opacity: 0, transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#ef4444', transform: 'scale(1.1)' }
                      }}
                      onClick={() => removeExistingImage(index)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </Paper>
                ))}

                {/* New Images Previews */}
                {newImagePreviews.map((preview, index) => (
                  <Paper
                    key={`new-${index}`}
                    elevation={0}
                    sx={{
                      aspectRatio: '1/1',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: '2px solid', borderColor: 'info.main',
                      position: 'relative',
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.05),
                      '&:hover .delete-btn': { opacity: 1 }
                    }}
                  >
                    <img
                      src={preview}
                      alt="new preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      className="delete-btn"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: alpha('#ef4444', 0.9), color: 'white',
                        opacity: 0, transition: 'all 0.2s',
                        '&:hover': { bgcolor: '#ef4444', transform: 'scale(1.1)' }
                      }}
                      onClick={() => removeNewImage(index)}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      bgcolor: 'rgba(24,144,255,0.9)', py: 0.5,
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 800, fontSize: '0.6rem' }}>NEW</Typography>
                    </Box>
                  </Paper>
                ))}

                {/* Upload Action */}
                <Box
                  sx={{
                    aspectRatio: '1/1',
                    borderRadius: 3,
                    border: '2px dashed #cbd5e1',
                    bgcolor: 'background.neutral',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': {
                      borderColor: 'info.main',
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.02),
                      '& .plus-icon': { color: 'info.main', transform: 'scale(1.1)' }
                    }
                  }}
                >
                  <Box
                    className="plus-icon"
                    sx={{
                      width: 40, height: 40, borderRadius: '50%',
                      bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'text.secondary', transition: 'all 0.2s'
                    }}
                  >
                    <Plus size={24} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Add Images</Typography>
                  <input
                    type="file" multiple accept="image/*"
                    onChange={handleImagesChange}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={onClose} disabled={loading} variant="outlined" sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3.5, py: 1, fontWeight: 700, borderRadius: 2 }}
          >
            {loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Product')}
          </Button>
        </AdminDialogActions>
      </form>
    </AdminDialog>
  );
};
export default ProductModal;
